import React, { useState } from 'react';
import { Container, Grid, Card, CardMedia, CardContent, Typography, Box, Button } from '@mui/material';
import Slider from 'react-slick';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import CampaignIcon from '@mui/icons-material/Campaign';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CodeIcon from '@mui/icons-material/Code';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import backgroundImage from '../images/service_bg_2.jpg';
import { styled } from '@mui/system';

const StyledSlider = styled(Slider)(({ theme }) => ({
  '& .slick-prev, & .slick-next': {
    zIndex: 1,
    width: 30,
    height: 30,
    '&:before': {
      fontSize: 30,
      color: 'white',
    },
  },
  '& .slick-prev': {
    left: -40,
    [theme.breakpoints.down('sm')]: {
      left: -20,
    },
  },
  '& .slick-next': {
    right: -40,
    [theme.breakpoints.down('sm')]: {
      right: -20,
    },
  },
}));

const Services = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleMouseEnter = (index) => {
    setHoveredCard(index);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
  };

  const cards = [
    {
      title: 'UI/UX Design',
      icon: <DesignServicesIcon />,
      description: 'Create engaging and intuitive interfaces that enhance user experience.',
    },
    {
      title: 'Digital Marketing',
      icon: <CampaignIcon />,
      description: 'Boost your online presence and reach your target audience effectively.',
    },
    {
      title: 'Business Analysis',
      icon: <BusinessCenterIcon />,
      description: 'Optimize your business processes and make data-driven decisions.',
    },
    {
      title: 'Software Services',
      icon: <CodeIcon />,
      description: 'Develop robust and scalable software solutions to meet your needs.',
    },
  ];

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Box
      sx={{
        marginTop: 4,
        position: 'relative',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '70vh',
      }}
    >
      <Container>
        <Grid container sx={{ paddingTop: 10 }} spacing={2}>
          <Grid item xs={12} md={8} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                fontSize: 20,
                color: '#684df4',
                fontWeight: 600,
              }}
            >
              OUR SERVICES
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                fontWeight: 800,
                marginTop: 2,
                color: '#fff',
              }}
            >
              We Provide Expert Solutions <br /> For <span style={{ color: '#7C4DFF' }}>Your Success</span>
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#7C4DFF',
                color: '#fff',
                fontFamily: "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                fontWeight: 'bold',
                padding: { xs: '8px 16px', md: '4px 8px' }, 
                textTransform: 'none',
                fontSize: 12,
                height: { md: '40px' },
                marginTop: { md: '20px' },
                '&:hover': {
                  backgroundColor: '#684df4',
                },
              }}
            >
              VIEW ALL SERVICES
              <ArrowForwardIcon sx={{ marginLeft: 1 }} />
            </Button>
          </Grid>
        </Grid>

        <StyledSlider {...settings} sx={{ marginTop: 4 }}>
          {cards.map((card, index) => (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
              <Card
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                sx={{
                  transform: hoveredCard === index ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.3s',
                  margin: '10px',
                  textAlign: 'center',
                  padding: 2,
                  boxShadow: 3,
                  backgroundColor: hoveredCard === index ? '#7C4DFF' : '#fff',
                  '&:hover': {
                    boxShadow: 6,
                    backgroundColor: '#7C4DFF',
                  },
                }}
              >
                <CardMedia>
                  <Grid sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                    <Grid
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: hoveredCard === index ? '#fff' : '#7f3ff2',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        transition: 'background-color 0.3s',
                      }}
                    >
                      {React.cloneElement(card.icon, {
                        sx: {
                          fontSize: 30,
                          color: hoveredCard === index ? '#7C4DFF' : '#fff',
                          transition: 'color 0.3s',
                        },
                      })}
                    </Grid>
                  </Grid>
                </CardMedia>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: hoveredCard === index ? '#fff' : '#7f3ff2',
                      transition: 'color 0.3s',
                      marginBottom: 1,
                      fontFamily: "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: hoveredCard === index ? '#fff' : '#000',
                      transition: 'color 0.3s',
                      fontFamily: "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                    }}
                  >
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </StyledSlider>
      </Container>
    </Box>
  );
};

export default Services;
